import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Typography
} from '@mui/material';

const AddImageDialog = ({ open, onClose, onSave, defaultSKU = '' }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    sku: '',
    external_url: '',
    sort_order: 0
  });

  useEffect(() => {
    if (defaultSKU) {
      setFormData(prev => ({
        ...prev,
        sku: defaultSKU
      }));
    }
  }, [defaultSKU]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear preview when URL changes
    if (name === 'external_url') {
      setPreviewUrl('');
    }
  };

  const handlePreview = () => {
    if (formData.external_url) {
      setPreviewLoading(true);
      setPreviewUrl(formData.external_url);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.sku || !formData.external_url) {
      setError('SKU and External URL are required');
      return;
    }

    // Validate URL
    try {
      new URL(formData.external_url);
    } catch {
      setError('Please enter a valid URL');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        sku: formData.sku.trim(),
        external_url: formData.external_url.trim(),
        sort_order: parseInt(formData.sort_order) || 0
      };

      console.log('Sending payload:', payload);

      const response = await fetch('https://elnaf.fi/new/api/add_product_image.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const responseText = await response.text();
      console.log('Response:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse JSON:', e);
        setError('Invalid response from server');
        setLoading(false);
        return;
      }

      if (response.ok && (data.message?.includes('uğurla') || data.message?.includes('yükləndi'))) {
        setSuccess(true);
        console.log('New image URL:', data.new_url);
        setTimeout(() => {
          onSave();
        }, 1500);
      } else {
        setError(data.message || 'Failed to add image');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && !success) {
      setError('');
      setSuccess(false);
      setPreviewUrl('');
      setPreviewLoading(false);
      setFormData({
        sku: '',
        external_url: '',
        sort_order: 0
      });
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Product Image</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Image uploaded successfully!
            </Alert>
          )}

          <TextField
            fullWidth
            label="SKU"
            name="sku"
            value={formData.sku}
            onChange={handleChange}
            margin="normal"
            required
            disabled={loading || success}
          />

          <TextField
            fullWidth
            label="External Image URL"
            name="external_url"
            value={formData.external_url}
            onChange={handleChange}
            margin="normal"
            required
            disabled={loading || success}
            placeholder="https://example.com/image.jpg"
            helperText="Enter the URL of the image you want to upload"
          />

          <Box display="flex" gap={2} alignItems="flex-end">
            <TextField
              fullWidth
              label="Sort Order"
              name="sort_order"
              type="number"
              value={formData.sort_order}
              onChange={handleChange}
              margin="normal"
              disabled={loading || success}
              InputProps={{ inputProps: { min: 0 } }}
            />
            <Button
              onClick={handlePreview}
              disabled={!formData.external_url || loading || success}
              sx={{ mb: 1 }}
            >
              Preview
            </Button>
          </Box>

          {previewUrl && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="subtitle2" gutterBottom>
                Image Preview:
              </Typography>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: 300,
                  mx: 'auto',
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  overflow: 'hidden',
                  backgroundColor: '#f5f5f5'
                }}
              >
                {previewLoading && (
                  <Box sx={{ p: 4 }}>
                    <CircularProgress />
                  </Box>
                )}
                <img
                  src={previewUrl}
                  alt="Preview"
                  style={{
                    width: '100%',
                    display: previewLoading ? 'none' : 'block'
                  }}
                  onLoad={() => setPreviewLoading(false)}
                  onError={() => {
                    setPreviewLoading(false);
                    setError('Failed to load image preview. Please check the URL.');
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading || success}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || success || !formData.sku || !formData.external_url}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {success ? 'Saved!' : 'Add Image'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddImageDialog;